import { reaction, toJS } from "mobx";
import { useCallback, useEffect, useRef, useState } from "react";

import { usePlayground } from "@/components/Playground/playground-store";
import useXRayPromptBlueprint from "@/queries/playground/useXRayBlueprint";

const DEBOUNCE_DELAY = 500;

const useXRayMode = () => {
  const playground = usePlayground();
  const [debouncedBlueprint, setDebouncedBlueprint] = useState(() =>
    JSON.parse(JSON.stringify(toJS(playground.blueprint))),
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debounceUpdateBlueprint = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedBlueprint(JSON.parse(JSON.stringify(toJS(playground.blueprint))));
    }, DEBOUNCE_DELAY);
  }, [playground.blueprint]);

  useEffect(() => {
    const dispose = reaction(
      () => JSON.stringify(toJS(playground.blueprint)),
      () => {
        debounceUpdateBlueprint();
      },
    );

    return () => {
      dispose();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debounceUpdateBlueprint, playground]);

  useEffect(() => {
    const dispose = reaction(
      () => playground.messagesContent,
      () => {
        debounceUpdateBlueprint();
      },
    );

    return () => {
      dispose();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debounceUpdateBlueprint, playground]);

  const selectedTemplateVersionId = playground.selectedTemplateVersion?.id;

  useEffect(() => {
    playground.disableXRayMode();
  }, [playground, selectedTemplateVersionId]);

  const { data, isLoading } = useXRayPromptBlueprint(debouncedBlueprint);

  useEffect(() => {
    if (data?.prompt_blueprint) {
      playground.setXRayBlueprint(data.prompt_blueprint);
    }
  }, [playground, data]);

  return {
    hasXRay: !!data?.expanded,
    isXRayMode: playground.isXRayMode,
    toggleXRayMode: playground.toggleXRayMode,
    isLoading,
  };
};

export default useXRayMode;
